import React from "react";
import { AuditLog } from "@common/types/auditLog";
import { useAccounts } from "@hooks/useAccounts";
import { useClasses } from "@api/class.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ActivityTimelineLog } from "@design-system/ActivityTimeline/ActivityTimeline";
import { AuditLogActor } from "../AuditLogActor";
import {
    AccrualEntityLabel,
    buildAccrualActivityContent,
} from "./AccrualActivityContent";
import { AccrualLabelResolver } from "./accrualValueFormat";

interface AccrualActivityRowProps {
    log: AuditLog;
    entityLabel: AccrualEntityLabel;
    entityId: number;
}

export const AccrualActivityRow: React.FC<AccrualActivityRowProps> = ({
    log,
    entityLabel,
    entityId,
}) => {
    const { accountsById: accrualAccountsById } = useAccounts({
        entityId,
        includeDisabled: true,
        ledgerBasis: AccountingLedgerBasis.accruals,
    });
    const { accountsById: cashAccountsById } = useAccounts({
        entityId,
        includeDisabled: true,
        ledgerBasis: AccountingLedgerBasis.cash,
    });
    const { classesMap } = useClasses();

    const resolver: AccrualLabelResolver = React.useMemo(
        () => ({
            account: (id) => {
                if (!id) {
                    return null;
                }
                return (
                    accrualAccountsById[id]?.name ??
                    cashAccountsById[id]?.name ??
                    null
                );
            },
            classLabel: (id) => {
                if (!id) {
                    return null;
                }
                return classesMap.get(id)?.label ?? null;
            },
        }),
        [accrualAccountsById, cashAccountsById, classesMap],
    );

    const author = (
        <AuditLogActor
            actorId={log.actorId}
            actorName={log.actorName}
            actorType={log.actorType}
            log={log}
        />
    );

    const entries = buildAccrualActivityContent({
        log,
        entityLabel,
        resolver,
    });

    return (
        <>
            {entries.map((content, index) => (
                <ActivityTimelineLog
                    key={`${log.id}-${index}`}
                    timestamp={log.createdAt}
                    details={content.details}
                >
                    <strong>{author}</strong> {content.summary}
                </ActivityTimelineLog>
            ))}
        </>
    );
};
